import {useCallback, useRef} from 'react';

/**
 * Devolverle el foco al riel del knob cuando se cierra el modal que salió de moverlo — **sólo si el
 * usuario lo movió con el TECLADO**.
 *
 * Las dos superficies del knob (el rail del Recorrido y el panel clásico) tenían su propia copia de
 * esto, y las dos refocuseaban siempre. El review del 27/09 (C-knob3-02) midió lo que eso deja:
 *
 * 1. **Con el foco en el riel, cada tecla GUARDA.** `→` sube un nivel y dispara el PUT: quien apretó
 *    «→» para aprobar la carta siguiente pasa de «Casi todo» a «Todo de una» y se abre otra oferta.
 *    `←`, `↑`, `↓`, `RePág`, `AvPág`, `Inicio` y `Fin` también mueven un `input[type=range]`, así que
 *    en el panel clásico —que no tiene teclado de mazo— alguien que quiere scrollear con `Fin` guarda
 *    la posición más permisiva.
 * 2. **No se ve dónde está el foco** si el modal se cerró con el mouse: `:focus-visible` no se
 *    enciende con un foco puesto por código.
 *
 * Con el gesto de origen, el caso que el QA quería arreglar (QA-VIS-knob2-09: quien navega con
 * teclado volvía al principio de la página) sigue cubierto, y el del mouse deja el foco donde estaba.
 */
export function useKnobRefocus() {
    const knobRef = useRef(null);
    const fromKeyboardRef = useRef(false);

    /** Lo llama el handler del cambio con el segundo argumento de `onChange` del control. */
    const rememberOrigin = useCallback((meta) => {
        fromKeyboardRef.current = !!(meta && meta.fromKeyboard);
    }, []);

    const refocusKnob = useCallback(() => {
        if (!fromKeyboardRef.current) return;
        const knob = knobRef.current;
        // El riel se deshabilita mientras guarda: enfocar un `disabled` no hace nada, pero pedirlo
        // igual dejaría la intención escrita en el lugar equivocado.
        if (knob && !knob.disabled) knob.focus();
    }, []);

    return {knobRef, refocusKnob, rememberOrigin};
}

export default useKnobRefocus;
